<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { downloadFile } from "@recordsure/unity-stk";

import { config } from "@/config";

import { authMachine } from "@/machines/auth-machine";

import { summaryNoteService } from "@/services";
import { $can } from "@/services/UtilitiesService";

import { useAnnotationStore, useBrandStore, useCaseFileStore, useSummaryNotesStore } from "@/stores";

import SummaryNotesItem from "@/components/CasePanels/SummaryNotes/SummaryNotesItem.vue";
import SummaryNotesAddItem from "@/components/CasePanels/SummaryNotes/SummaryNotesAddItem.vue";
import SummaryNotesCategoryThemesTooltip from "@/components/Tooltip/SummaryNotesCategoryThemesTooltip.vue";

import { ModuleStatus } from "@/models/ModuleStatus";

import type { Metadata } from "@/models/Metadata";
import type DialogueIdentity from "@/models/DialogueIdentity";
import type { InjectedCaseRouteParams } from "@/hooks/use-case-route-params";
import type { SummaryNote, SummaryNoteCitation, SummaryNoteModified } from "@/models/SummaryNote";

withDefaults(defineProps<{ file: Metadata; routeParams: InjectedCaseRouteParams }>(), {  })

const summaryNotesStore = useSummaryNotesStore();

const summaryNotesItems = ref<Array<InstanceType<typeof SummaryNotesItem>>>([]);
const addedReferences = ref<string[]>([]);
const isAdding = ref<{ category: string; state: boolean }>({ category: "", state: false });
const isDeleting = ref<{ category: string; state: boolean }>({ category: "", state: false });
const isSorting = ref<{ category: string; state: boolean; reference: string }>({
  category: "",
  reference: "",
  state: false,
});

const exportingState = ref<"DONE" | "ERROR" | "PROCESSING">();
const canExportSummaryNotes = computed(
  () => $can("manageMeetingNotes") && (useBrandStore().viewerConfig.summaryNotes?.export?.enabled ?? false),
);

const canManageSummaryNotes = computed(() => $can("manageMeetingNotes") && $can("annotate"));

const summaryNotes = computed(
  () => summaryNotesStore.rawSummaryNotes[__props.file.fileReference] ?? { status: ModuleStatus.Initial, items: [] },
);

const groupedSummaryNotes = computed<SummaryNoteSetup[]>(() => {
  if (summaryNotesConfig.value == null) return [];

  const nameMap = ["Supporting", "Contrary", "Association"] as const;
  const summaryNotesWithCitations = summaryNotes.value.items.map((dp) => {
    const result = dp.citations?.reduce<Record<number, SummaryNoteCitation[]>>((acc, citation) => {
      const key = citation.supporting == null ? 2 : citation.supporting ? 0 : 1;
      return { ...acc, [key]: [...(acc[key] ?? []), citation] };
    }, {});

    return {
      ...dp,
      citations: Object.entries(result ?? {})
        .filter(([, citations]) => citations.length > 0)
        .sort((a, b) => +a[0] - +b[0])
        .map<[(typeof nameMap)[number], SummaryNoteCitation[]]>(([key, citations]) => [nameMap[+key], citations]),
    };
  });

  const fileThemes = new Set(
    useAnnotationStore().unfilteredAnnotations.flatMap((mark) =>
      mark.fileReference !== __props.file.fileReference || /rejected/i.test(mark.status ?? "")
        ? []
        : (mark.evidenceThemes?.map((e) => e.evidenceTheme) ?? []),
    ),
  );

  const maxSummaryNotePosition = Math.max(0, ...summaryNotes.value.items.map((point) => point.position));

  return summaryNotesConfig.value.labels.map((dpc) => {
    const filteredSummaryNotes = summaryNotesWithCitations.filter(
      (rdp) => rdp.evidenceTheme != null && dpc.themes.includes(rdp.evidenceTheme),
    );

    return {
      name: dpc.name,
      position: dpc.position,
      summaryNotes: filteredSummaryNotes,
      showEvidenceThemes: dpc.showEvidenceThemes,
      themes: dpc.themes.filter((theme) => fileThemes.has(theme)),
      themeToAdd: dpc.themes[0],
      maxSummaryNotePosition: isNaN(maxSummaryNotePosition) ? 0 : maxSummaryNotePosition,
    };
  });
});

const summaryNotesConfig = computed(() =>
  useBrandStore().viewerConfig.dataPoints?.find((f) => f.usage === "Meeting Notes"),
);

const highlightColors = computed(() => useBrandStore().viewerConfig.evidenceThemes?.highlightColors ?? {});

const supportsSummaryNotes = computed(() => __props.file.fileType === "audio" || __props.file.fileType === "ac5");

const speakerIdToName = computed<Map<string, string>>(() => {
  try {
    const identities = JSON.parse(__props.file.dialogueIdentities ?? "{}") as Record<string, DialogueIdentity | undefined>;

    return Object.entries(identities).reduce<Map<string, string>>((acc, [key, identity]) => {
      if (identity != null) acc.set(`Speaker ${key}`, identity.name || `${identity.role} ${identity.index ?? key}`);
      return acc;
    }, new Map());
  } catch (error) {
    return new Map();
  }
});

watch(
  supportsSummaryNotes,
  () => {
    if (!supportsSummaryNotes.value) return;
    summaryNotesStore.load(__props.file.fileReference);
  },
  { immediate: true },
);

async function moveSummaryNote(reference: string, category: string, dir: "up" | "down") {
  try {
    isSorting.value = { category: "", reference: "", state: false };

    const categorySummaryNote = groupedSummaryNotes.value.find((dp) => dp.name === category);
    if (categorySummaryNote == null) return;

    const cdpIdx = categorySummaryNote.summaryNotes.findIndex((dp) => dp.reference === reference);

    if (
      // Data point not found
      cdpIdx < 0 ||
      // First item in list and moving up
      (dir === "up" && cdpIdx === 0) ||
      // Last item in list and moving down
      (dir === "down" && cdpIdx === categorySummaryNote.summaryNotes.length - 1)
    ) {
      return;
    }

    const leftDataPoint = categorySummaryNote?.summaryNotes[cdpIdx];
    const rightDataPoint = categorySummaryNote?.summaryNotes[cdpIdx + (dir === "up" ? -1 : 1)];

    isSorting.value = { category, reference, state: true };

    await summaryNotesStore.swapSummaryNotePosition(__props.file.fileReference, leftDataPoint, rightDataPoint);
  } finally {
    isSorting.value = { category: "", reference: "", state: false };
  }
}

async function deleteSummaryNote(reference: string, category: string) {
  isDeleting.value = { category, state: true };
  await summaryNotesStore.deleteSummaryNote(__props.file.fileReference, reference); // Delete Data point
  isDeleting.value = { category: "", state: false };
}

async function getSummaryNotes(dp: SummaryNote, added: boolean) {
  if (added) addedReferences.value = [...addedReferences.value, dp.reference];

  summaryNotesStore.storeSummaryNote(__props.file.fileReference, dp);
  setAddingState({ state: false });
  await summaryNotesStore.load(__props.file.fileReference);
}

function setSelectedThemes(themes: string[]) {
  useCaseFileStore().setSelectedThemes(themes);
}

function setAddingState(arg: { state: false } | { state: true; category: string }) {
  if (arg.state) isAdding.value = arg;
  else isAdding.value = { category: "", state: false };
}

function downloadSummaryNotes() {
  const { client, brand, casename } = __props.routeParams;

  const token = authMachine.getSnapshot().context.token?.access_token;
  if (token == null) return;

  const controller = new AbortController();

  downloadFile(
    `${config.apiUrl}/api/summaryNotes/${client}/${brand}/${encodeURIComponent(casename)}/${__props.file.fileReference}/docx`,
    { accessToken: token, ssoApiUrl: config.ssoApiUrl, signal: controller.signal },
  );
}

async function exportSummaryNotes() {
  const { client, brand, casename } = __props.routeParams;

  try {
    exportingState.value = "PROCESSING";
    await summaryNoteService.exportNotes(client, brand, casename, __props.file.fileReference);
    exportingState.value = "DONE";
  } catch (error) {
    console.error(error);
    exportingState.value = "ERROR";
  } finally {
    setTimeout(() => (exportingState.value = undefined), 1500);
  }
}

interface SummaryNoteSetup {
  name: string;
  themes: string[];
  themeToAdd: string;
  position: number;
  showEvidenceThemes: boolean;
  summaryNotes: SummaryNoteModified[];
  maxSummaryNotePosition: number;
}
</script>
<template>
  <div v-if="!supportsSummaryNotes" class="tw-grid tw-size-full tw-place-items-center">
    <p>This record does not support summary notes.</p>
  </div>
  <div v-else-if="summaryNotes.items.length < 1" class="tw-grid tw-size-full tw-place-items-center">
    <p class="tw-max-w-sm tw-text-pretty">
      <template v-if="summaryNotes.status === ModuleStatus.Ready">No Summary Notes found</template>
      <template v-else-if="summaryNotes.status === ModuleStatus.Error">
        An error occurred while fetching the Summary Notes
      </template>
      <template v-else>Loading...</template>
    </p>
  </div>
  <div v-else class="tw-flex tw-h-full tw-flex-col">
    <div
      class="tw-flex tw-flex-wrap tw-items-center tw-justify-end tw-gap-x-1.5 tw-gap-y-1 tw-bg-gray-800 tw-p-1.5 empty:tw-hidden"
    >
      <button v-if="canExportSummaryNotes" type="button" class="tw-btn-md-gray invert" @click="exportSummaryNotes">
        <template v-if="exportingState === 'PROCESSING'">
          <i class="fas fa-spinner tw-animate-spin"></i>
          Requesting Export
        </template>
        <template v-else-if="exportingState === 'DONE'">
          <i class="fas fa-check-circle"></i>
          Requested Export
        </template>
        <template v-else-if="exportingState === 'ERROR'">
          <i class="fas fa-triangle-exclamation"></i>
          Error Requesting Export
        </template>
        <template v-else>
          <i class="fas fa-file-export"></i>
          Request Export
        </template>
      </button>
      <button v-if="$can('downloadMeetingNotes')" type="button" class="tw-btn-md-gray" @click="downloadSummaryNotes">
        <i class="fas fa-download"></i>
        Download
      </button>
    </div>
    <div class="tw-flex tw-flex-1 tw-flex-col tw-gap-2.5 tw-overflow-y-auto tw-bg-white [--padding:theme(spacing.4)]">
      <div class="tw-grid tw-grid-cols-[min-content_minmax(0,1fr)_max-content] tw-py-[--padding]">
        <template v-for="(item, categoryIdx) in groupedSummaryNotes" :key="item.name">
          <div class="tw-col-span-full tw-flex tw-items-center tw-justify-start tw-gap-2 tw-px-[--padding]">
            <h3 tabindex="0" class="tw-text-base tw-font-semibold">{{ item.name }}</h3>
            <div aria-hidden="true" class="tw-flex-1" />
            <SummaryNotesCategoryThemesTooltip
              v-if="item.showEvidenceThemes && item.themes.length > 0"
              align="end"
              :item="item.themes"
              :root="{ disabled: item.themes.length < 2 }"
            >
              <button
                class="tw-flex tw-min-w-max tw-cursor-pointer tw-items-center tw-justify-end tw-gap-1 tw-rounded-md tw-px-2 tw-py-1 tw-text-gray-600 hover:tw-bg-gray-800/75 hover:tw-text-white"
                @click="setSelectedThemes(item.themes)"
              >
                <span class="tw-size-2 tw-rounded-md" :style="{ backgroundColor: highlightColors[item.themes[0]] }" />
                <p class="tw-underline tw-underline-offset-2">
                  {{ `${item.themes[0]}${item.themes.length > 1 ? ` + ${item.themes.length - 1} more` : ""}` }}
                </p>
              </button>
            </SummaryNotesCategoryThemesTooltip>
          </div>
          <div class="tw-col-span-full tw-grid tw-grid-cols-subgrid tw-items-center">
            <SummaryNotesItem
              v-for="(point, index) in item.summaryNotes"
              ref="summaryNotesItems"
              :key="point.reference"
              :summary-note="point"
              :category="item.name"
              :show-citations="categoryIdx > 0"
              :is-added="addedReferences.includes(point.reference)"
              :is-first="index === 0"
              :is-last="index === item.summaryNotes.length - 1"
              :can-manage-summary-notes="canManageSummaryNotes"
              :is-moving="isSorting.state && isSorting.reference === point.reference"
              :is-any-mutating="
                isAdding.state ||
                isSorting.state ||
                isDeleting.state ||
                summaryNotesItems.find((n) => n.reference !== point.reference && n.isEditing) != null
              "
              :is-any-mutating-in-category="
                isDeleting.state ||
                (isAdding.state && isAdding.category === item.name) ||
                (isSorting.state && isSorting.category === item.name) ||
                summaryNotesItems.find(
                  (n) => n.category === item.name && n.reference !== point.reference && n.isEditing,
                ) != null
              "
              :speaker-id-to-name="speakerIdToName"
              @refresh-list="(note) => getSummaryNotes(note, false)"
              @delete="() => deleteSummaryNote(point.reference, item.name)"
              @move-up="() => moveSummaryNote(point.reference, item.name, 'up')"
              @move-down="() => moveSummaryNote(point.reference, item.name, 'down')"
            />
            <div
              v-if="!(isAdding.state && isAdding.category === item.name) && item.summaryNotes.length === 0"
              class="tw-col-span-full tw-grid tw-select-none tw-place-items-center tw-text-pretty tw-py-1 tw-text-center tw-font-medium tw-text-gray-800"
            >
              No items found
            </div>
            <div v-if="canManageSummaryNotes" class="tw-contents *:tw-col-span-full *:tw-px-[--padding]">
              <SummaryNotesAddItem
                v-if="isAdding.state && isAdding.category === item.name"
                :theme="item.themeToAdd"
                :file-reference="file.fileReference"
                :last-position="item.maxSummaryNotePosition"
                @refresh-list="(note) => getSummaryNotes(note, true)"
                @stop-adding="() => setAddingState({ state: false })"
              />
              <div v-else>
                <button
                  type="button"
                  :disabled="isAdding.state"
                  class="tw-rounded-md tw-px-2 tw-py-1 tw-text-gray-600 tw-underline tw-underline-offset-2 hover:tw-bg-gray-800/75 hover:tw-text-white disabled:tw-cursor-auto"
                  @click="() => setAddingState({ state: true, category: item.name })"
                >
                  Add new item
                </button>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>
